import { useToast } from "@/hooks/use-toast";
import { isAdmin as checkIsAdmin } from "@/services/authService";

export const useSitemapFetch = () => {
  const { toast } = useToast();

  /**
   * Clean XML content to ensure proper validation
   */
  const cleanXmlContent = (content: string): string => {
    if (!content) return '';
    
    // Trim the string and ensure XML declaration is at the start
    let cleanContent = content.trim();
    const xmlDeclIndex = cleanContent.indexOf('<?xml');
    
    if (xmlDeclIndex > 0) {
      // XML declaration exists but not at the start
      console.log(`Found XML declaration at position ${xmlDeclIndex}, removing content before it`);
      cleanContent = cleanContent.substring(xmlDeclIndex);
    }
    
    return cleanContent;
  };

  /**
   * Save sitemap and robots.txt files to domain root
   */
  const saveSitemapToPublic = async (data: { sitemapUrl: string, robotsUrl: string }): Promise<boolean> => {
    try {
      const isAdmin = await checkIsAdmin();
      if (!isAdmin) {
        console.error('User is not an admin');
        toast({
          title: "Access Denied",
          description: "Admin privileges required for this action",
          variant: "destructive",
        });
        return false;
      }

      console.log('Sitemap and robots.txt are generated as static root files; no copy required.');
      return true;
    } catch (error) {
      console.error('Error copying sitemap files to domain root:', error);
      toast({
        title: "Error Copying Sitemap Files",
        description: `${error instanceof Error ? error.message : String(error)}`,
        variant: "destructive",
      });
      return false;
    }
  };

  /**
   * Fetch sitemap from domain with enhanced error handling and analysis
   */
  const fetchDomainSitemap = async (cacheBust: string): Promise<string> => {
    let domainRetryCount = 0;
    const maxRetries = 3;
    let domainSitemapContent = '';
    
    while (domainRetryCount < maxRetries) {
      try {
        console.log(`Checking domain sitemap (attempt ${domainRetryCount + 1}/${maxRetries})...`);
        
        // Add timeout to prevent hanging requests
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await fetch('/sitemap.xml' + cacheBust, {
          signal: controller.signal,
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          console.warn(`Domain sitemap not accessible at /sitemap.xml (attempt ${domainRetryCount + 1}), status: ${response.status}`);
          domainRetryCount++;
          
          if (domainRetryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 300));
            continue;
          }
          break;
        }
        
        const rawContent = await response.text();
        domainSitemapContent = cleanXmlContent(rawContent);
        
        // Log detailed content information
        console.log('Domain sitemap response headers:', Object.fromEntries(response.headers.entries()));
        console.log('Domain sitemap content first 100 chars:', domainSitemapContent.substring(0, 100));
        console.log('Domain sitemap length:', domainSitemapContent.length);
        break;
      } catch (e) {
        console.warn(`Error checking domain sitemap (attempt ${domainRetryCount + 1}):`, e);
        domainRetryCount++;
        
        if (domainRetryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
    }
    
    return domainSitemapContent;
  };

  /**
   * Fetch sitemap from storage with enhanced error handling and analysis
   */
  const fetchStorageSitemap = async (sitemapUrl: string, cacheBust: string): Promise<string> => {
    let storageSitemapContent = '';
    
    if (sitemapUrl) {
      try {
        console.log('Checking storage sitemap...');
        
        // Add timeout to prevent hanging requests
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await fetch(sitemapUrl + cacheBust, {
          signal: controller.signal,
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const rawContent = await response.text();
          storageSitemapContent = cleanXmlContent(rawContent);
          
          // Log response information
          console.log('Storage sitemap response headers:', Object.fromEntries(response.headers.entries()));
          console.log('Storage sitemap content type:', response.headers.get('content-type'));
          console.log('Storage sitemap content first 100 chars:', storageSitemapContent.substring(0, 100));
          console.log('Storage sitemap length:', storageSitemapContent.length);
        } else {
          console.error('Storage sitemap not accessible, status:', response.status, response.statusText);
        }
      } catch (e) {
        console.warn('Error checking storage sitemap:', e);
      }
    }
    
    return storageSitemapContent;
  };

  return {
    fetchDomainSitemap,
    fetchStorageSitemap,
    cleanXmlContent,
    saveSitemapToPublic
  };
};
